import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { IoCloseOutline } from "react-icons/io5";
import { filterNcsColors } from "./filterNcsColors";
import { findNcsColorByHex, getNcsShortCode } from "./findNcsColorByHex";
import { NCS_COLORS, type NcsColorOption } from "./ncsColors";

/** Matches Flowbite editor-toolbar tooltips; much faster than native `title`. */
const SWATCH_TOOLTIP_DELAY_MS = 100;
/** How many swatches to mount initially / per scroll page (4-column grid). */
export const NCS_SWATCH_PAGE_SIZE = 48;
/** Load the next page when the user is this close to the bottom. */
const SCROLL_LOAD_THRESHOLD_PX = 48;

type NcsColorPickerProps = {
  /** Currently applied fill hex (`#RRGGBB`), used to highlight a matching swatch. */
  value?: string;
  onSelect: (hex: string, option: NcsColorOption) => void;
  /** Clears the NCS selection and resets fill (typically to default white). */
  onClear?: () => void;
  disabled?: boolean;
  /**
   * Whether the swatch grid starts expanded.
   * Defaults to collapsed so Fill/Lighting do not show the full grid until opened.
   */
  defaultOpen?: boolean;
  /**
   * NCS colors already used in the plan. Shown above the full grid when search
   * is empty so re-applying the same color does not require searching again.
   */
  pinnedColors?: NcsColorOption[];
};

type SwatchTooltipState = {
  content: string;
  top: number;
  left: number;
};

/**
 * Card-style NCS palette: selected code stays visible; searchable swatch grid
 * toggles via the same +/- header control as other property-panel sections.
 * The full list is windowed and grows as the user scrolls (avoids mounting ~2k nodes).
 */
export const NcsColorPicker: React.FC<NcsColorPickerProps> = ({
  value,
  onSelect,
  onClear,
  disabled = false,
  defaultOpen = false,
  pinnedColors = [],
}) => {
  const { t } = useTranslation();
  const searchId = useId();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(defaultOpen);
  /** Prefer the exact option the user clicked when several share a hex. */
  const [lastSelected, setLastSelected] = useState<NcsColorOption | null>(null);
  const [tooltip, setTooltip] = useState<SwatchTooltipState | null>(null);
  /** How many filtered swatches are currently mounted in the scroll grid. */
  const [visibleCount, setVisibleCount] = useState(NCS_SWATCH_PAGE_SIZE);
  const tooltipTimeoutRef = useRef<number | null>(null);

  const filteredColors = useMemo(
    () => filterNcsColors(NCS_COLORS, search),
    [search],
  );

  /** Pinned row only while browsing (empty search); search takes the full grid. */
  const showPinnedColors = search.trim().length === 0 && pinnedColors.length > 0;

  /** Reset the window whenever search changes (browse and filter both start small). */
  useEffect(() => {
    setVisibleCount(NCS_SWATCH_PAGE_SIZE);
  }, [search]);

  const visibleColors = useMemo(
    () => filteredColors.slice(0, visibleCount),
    [filteredColors, visibleCount],
  );

  const normalizedValue = value?.toUpperCase();
  const matchedByHex = useMemo(() => findNcsColorByHex(value), [value]);

  const selectedOption =
    lastSelected &&
    normalizedValue &&
    lastSelected.hex.toUpperCase() === normalizedValue
      ? lastSelected
      : matchedByHex;

  useEffect(() => {
    if (
      lastSelected &&
      (!normalizedValue || lastSelected.hex.toUpperCase() !== normalizedValue)
    ) {
      setLastSelected(null);
    }
  }, [lastSelected, normalizedValue]);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current !== null) {
        window.clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const clearTooltip = () => {
    if (tooltipTimeoutRef.current !== null) {
      window.clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setTooltip(null);
  };

  /**
   * One shared portal tooltip for the whole grid (avoids ~2k Flowbite wrappers
   * while matching toolbar tooltip look and a short show delay).
   */
  const showSwatchTooltip = (target: HTMLElement, content: string) => {
    if (tooltipTimeoutRef.current !== null) {
      window.clearTimeout(tooltipTimeoutRef.current);
    }
    tooltipTimeoutRef.current = window.setTimeout(() => {
      const rect = target.getBoundingClientRect();
      setTooltip({
        content,
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    }, SWATCH_TOOLTIP_DELAY_MS);
  };

  const selectedShortCode = selectedOption
    ? getNcsShortCode(selectedOption)
    : undefined;

  const handleClear = () => {
    setLastSelected(null);
    onClear?.();
  };

  const selectColor = (color: NcsColorOption) => {
    clearTooltip();
    setLastSelected(color);
    onSelect(color.hex, color);
  };

  /**
   * Shared swatch button for pinned + windowed grids (same selection / tooltip UX).
   */
  const renderSwatch = (color: NcsColorOption, keyPrefix = "") => {
    const isSelected =
      selectedOption?.name === color.name ||
      (!selectedOption && normalizedValue === color.hex.toUpperCase());
    const shortName = getNcsShortCode(color);
    return (
      <button
        key={`${keyPrefix}${color.name}`}
        type="button"
        role="option"
        aria-selected={isSelected}
        aria-label={color.name}
        disabled={disabled}
        onMouseEnter={(event) =>
          showSwatchTooltip(event.currentTarget, color.name)
        }
        onMouseLeave={clearTooltip}
        onFocus={(event) =>
          showSwatchTooltip(event.currentTarget, color.name)
        }
        onBlur={clearTooltip}
        onClick={() => selectColor(color)}
        className={`flex flex-col overflow-hidden rounded border bg-white text-left transition-shadow disabled:cursor-not-allowed disabled:opacity-50 ${
          isSelected
            ? "border-black ring-2 ring-black ring-offset-1"
            : "border-gray-300 hover:border-gray-500"
        }`}
      >
        <span
          className="h-10 w-full border-b border-gray-200"
          style={{ backgroundColor: color.hex }}
          aria-hidden
        />
        <span className="truncate px-1 py-1 text-center font-mono text-[10px] leading-tight text-gray-800">
          {shortName}
        </span>
      </button>
    );
  };

  /**
   * Clears the tooltip and appends the next swatch page when scrolled near the bottom.
   */
  const handleSwatchGridScroll = (
    event: React.UIEvent<HTMLDivElement>,
  ) => {
    clearTooltip();
    if (visibleCount >= filteredColors.length) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollTop + clientHeight < scrollHeight - SCROLL_LOAD_THRESHOLD_PX) {
      return;
    }
    setVisibleCount((prev) =>
      Math.min(prev + NCS_SWATCH_PAGE_SIZE, filteredColors.length),
    );
  };

  return (
    <div className="my-2 rounded-md border border-gray-300 bg-white">
      <div className="border-b border-gray-200 px-2.5 py-2">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between text-left disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setOpen((prev) => !prev)}
          disabled={disabled}
          aria-expanded={open}
          aria-label={open ? t("propertyPanel.ncsHide") : t("propertyPanel.ncsShow")}
        >
          <div className="text-[12px] font-medium text-gray-800">
            {t("propertyPanel.ncsPalette")}
          </div>
          <div className="flex flex-row items-center justify-center text-sm text-gray-400">
            {open ? "−" : "+"}
          </div>
        </button>
      </div>

      <div className="px-2.5 py-2" aria-live="polite">
        <div className="flex items-center gap-2">
          {/*
            Matched NCS hex → solid swatch. No match → white + red slash so it
            is obvious no NCS color is selected (Color field still shows the fill).
          */}
          <span
            className="relative h-7 w-7 shrink-0 overflow-hidden rounded-sm border border-gray-300 bg-white"
            data-testid="ncs-selected-swatch"
            data-ncs-matched={selectedOption ? "true" : "false"}
            aria-hidden
          >
            {selectedOption ? (
              <span
                className="absolute inset-0"
                style={{ backgroundColor: selectedOption.hex }}
              />
            ) : (
              <span className="absolute left-1/2 top-1/2 h-[141%] w-0.5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-red-500" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-medium text-gray-500">
              {t("propertyPanel.ncsSelected")}
            </div>
            <div
              className="truncate font-mono text-[12px] font-medium text-gray-900"
              onMouseEnter={
                selectedOption
                  ? (event) =>
                      showSwatchTooltip(event.currentTarget, selectedOption.name)
                  : undefined
              }
              onMouseLeave={selectedOption ? clearTooltip : undefined}
            >
              {selectedShortCode ?? t("propertyPanel.ncsNoneSelected")}
            </div>
          </div>
          {selectedOption && onClear && (
            <button
              type="button"
              className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleClear}
              disabled={disabled}
              aria-label={t("propertyPanel.ncsClear")}
              title={t("propertyPanel.ncsClear")}
            >
              <IoCloseOutline className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-200 px-2.5 pb-2.5 pt-2">
          <label className="sr-only" htmlFor={searchId}>
            {t("propertyPanel.ncsSearchPlaceholder")}
          </label>
          <input
            id={searchId}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("propertyPanel.ncsSearchPlaceholder")}
            className="mb-2 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            disabled={disabled}
          />
          {showPinnedColors && (
            <div className="mb-2">
              <div className="mb-1.5 text-[11px] font-medium text-gray-500">
                {t("propertyPanel.ncsUsedInPlan")}
              </div>
              <div
                className="grid grid-cols-4 gap-2"
                role="listbox"
                aria-label={t("propertyPanel.ncsUsedColors")}
              >
                {pinnedColors.map((color) => renderSwatch(color, "pinned-"))}
              </div>
            </div>
          )}
          {filteredColors.length === 0 ? (
            <div className="py-6 text-center text-xs text-gray-500">
              {t("propertyPanel.ncsNoResults", { query: search })}
            </div>
          ) : (
            <>
              {showPinnedColors && (
                <div className="mb-1.5 text-[11px] font-medium text-gray-500">
                  {t("propertyPanel.ncsAllColors")}
                </div>
              )}
              <div
                className="grid max-h-56 grid-cols-4 gap-2 overflow-y-auto pr-1"
                role="listbox"
                aria-label={t("propertyPanel.ncsColors")}
                onScroll={handleSwatchGridScroll}
              >
                {visibleColors.map((color) => renderSwatch(color))}
              </div>
            </>
          )}
        </div>
      )}

      {tooltip &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white shadow-sm"
            style={{ top: tooltip.top, left: tooltip.left }}
          >
            {tooltip.content}
            <span
              className="absolute left-1/2 top-full -mt-px -translate-x-1/2 border-x-[6px] border-t-[6px] border-x-transparent border-t-gray-900"
              aria-hidden
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default NcsColorPicker;
